/**
 * Runner UI for the med-dictate (FR) live-transcription WER benchmark: streams each
 * selected clip through each selected model over the real "/asr" WebSocket (simulated
 * microphone input), then hands the accumulated results to MedDictateBenchmarkResults.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Play, Square, Loader2 } from "lucide-react";
import {
  getApiBaseUrl,
  getWsBaseUrl,
  listBenchmarkDatasets,
  getDatasetSamples,
  fetchSampleAudioBlob,
} from "../services/benchmarkService";
import { runLiveClipThroughModel } from "../hooks/useLiveModelBenchmark";
import {
  calculateWER,
  categorizeWER,
  stripDictationMarkers,
} from "../utils/werCalculator";
import werService from "../services/werService";
import MedDictateBenchmarkResults from "./MedDictateBenchmarkResults";

const DATASET_ID = "med-dictate-fr";

const panelClass =
  "rounded-2xl border border-white/10 bg-white/5 backdrop-blur-2xl shadow-[0_25px_80px_rgba(2,6,23,0.55)] p-6";
const checkboxRowClass =
  "flex items-start gap-3 rounded-xl border border-white/10 bg-slate-950/40 px-3 py-2 text-sm text-slate-200";

const MedDictateBenchmark = () => {
  const [apiToken, setApiToken] = useState("");
  const [models, setModels] = useState([]);
  const [selectedModelIds, setSelectedModelIds] = useState([]);
  const [samples, setSamples] = useState([]);
  const [selectedSampleIds, setSelectedSampleIds] = useState([]);
  const [datasetInfo, setDatasetInfo] = useState(null);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [catalogError, setCatalogError] = useState(null);

  const [isRunning, setIsRunning] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [log, setLog] = useState([]);
  const [results, setResults] = useState(() =>
    werService.getLiveBenchmarkResults(),
  );
  const cancelRequestedRef = useRef(false);

  useEffect(() => {
    const stored = localStorage.getItem("saved_api_token");
    if (stored) setApiToken(stored);
  }, []);

  const appendLog = useCallback((message, level = "info") => {
    setLog((prev) =>
      [
        {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          message,
          level,
          timestamp: new Date().toLocaleTimeString(),
        },
        ...prev,
      ].slice(0, 200),
    );
  }, []);

  const loadCatalog = useCallback(async () => {
    if (!apiToken) return;
    setLoadingCatalog(true);
    setCatalogError(null);
    try {
      const [datasets, sampleList, modelsResponse] = await Promise.all([
        listBenchmarkDatasets(apiToken),
        getDatasetSamples(apiToken, DATASET_ID),
        fetch(`${getApiBaseUrl()}/v1/models`, {
          headers: { Authorization: `Bearer ${apiToken}` },
        }).then((r) => r.json()),
      ]);

      setDatasetInfo(datasets.find((d) => d.id === DATASET_ID) || null);
      setSamples(sampleList);
      setSelectedSampleIds(sampleList.map((s) => s.id));

      const liveModels = (modelsResponse.details || []).filter(
        (m) => m.live_capable !== false && m.available !== false,
      );
      setModels(liveModels);
      setSelectedModelIds(liveModels.map((m) => m.id));
    } catch (error) {
      setCatalogError(error.message || "Impossible de charger le catalogue");
    } finally {
      setLoadingCatalog(false);
    }
  }, [apiToken]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const toggleId = (list, setList, id) => {
    setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  };

  const runBenchmark = async () => {
    if (
      !apiToken ||
      selectedModelIds.length === 0 ||
      selectedSampleIds.length === 0
    )
      return;

    setIsRunning(true);
    cancelRequestedRef.current = false;
    const wsBaseUrl = getWsBaseUrl();
    const newResults = [];

    const samplesToRun = samples.filter((s) =>
      selectedSampleIds.includes(s.id),
    );

    for (const sample of samplesToRun) {
      if (cancelRequestedRef.current) break;
      let audioBlob;
      try {
        setCurrentTask(`Telechargement de l'audio "${sample.id}"...`);
        audioBlob = await fetchSampleAudioBlob(apiToken, DATASET_ID, sample.id);
      } catch (error) {
        appendLog(
          `Echec du telechargement audio pour "${sample.id}": ${error.message}`,
          "error",
        );
        continue;
      }

      for (const modelId of selectedModelIds) {
        if (cancelRequestedRef.current) break;
        setCurrentTask(`Extrait "${sample.id}" -> modele "${modelId}"...`);
        try {
          const { hypothesisText, elapsedMs } = await runLiveClipThroughModel({
            wsBaseUrl,
            apiToken,
            modelId,
            audioBlob,
            isCancelled: () => cancelRequestedRef.current,
          });
          const werResult = calculateWER(
            stripDictationMarkers(sample.reference_text),
            stripDictationMarkers(hypothesisText),
          );
          const row = {
            sampleId: sample.id,
            modelId,
            referenceText: sample.reference_text,
            hypothesisText,
            wer: werResult.wer,
            editDistance: werResult.editDistance,
            refLength: werResult.refLength,
            hypLength: werResult.hypLength,
            category: categorizeWER(werResult.wer),
            durationMs: elapsedMs,
          };
          newResults.push(row);
          setResults((prev) => [
            ...prev.filter(
              (r) =>
                !(r.sampleId === row.sampleId && r.modelId === row.modelId),
            ),
            row,
          ]);
          appendLog(
            `"${sample.id}" x "${modelId}": WER ${werResult.wer}%`,
            "success",
          );
        } catch (error) {
          appendLog(
            `"${sample.id}" x "${modelId}" a echoue: ${error.message}`,
            "error",
          );
        }
      }
    }

    setCurrentTask(null);
    setIsRunning(false);
    werService.saveLiveBenchmarkResults(
      results
        .filter(
          (r) =>
            !newResults.some(
              (n) => n.sampleId === r.sampleId && n.modelId === r.modelId,
            ),
        )
        .concat(newResults),
    );
  };

  const cancelBenchmark = () => {
    cancelRequestedRef.current = true;
    appendLog("Annulation demandee...", "warning");
  };

  return (
    <div className="space-y-6">
      <div className={panelClass}>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-cyan-200/70">
              Benchmark HF
            </p>
            <h2 className="mt-2 text-2xl font-semibold text-white">
              Med-Dictate (FR) — WER en transcription live
            </h2>
            <p className="text-sm text-slate-300">
              Rejoue chaque extrait audio comme une entree microphone simuliee,
              pour chaque modele selectionne, via le WebSocket de transcription
              live.
            </p>
          </div>
        </div>

        <div className="mt-4">
          <label className="text-xs uppercase tracking-[0.3em] text-slate-400">
            Token API
          </label>
          <input
            type="text"
            value={apiToken}
            onChange={(e) => setApiToken(e.target.value)}
            placeholder="Token API"
            className="mt-1 w-full rounded-xl border border-white/20 bg-slate-950/40 px-3 py-2 text-sm text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-indigo-400/70"
          />
        </div>

        {catalogError && (
          <p className="mt-3 text-sm text-red-300">{catalogError}</p>
        )}

        {datasetInfo && (
          <p className="mt-3 text-xs text-slate-400">
            Source:{" "}
            <a
              href={datasetInfo.source_url}
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              {datasetInfo.source_url}
            </a>{" "}
            — {datasetInfo.license_note}
          </p>
        )}

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div>
            <h3 className="text-sm font-semibold text-white">
              Modeles ({selectedModelIds.length}/{models.length})
            </h3>
            <div className="mt-2 space-y-2 max-h-64 overflow-y-auto pr-1">
              {models.map((model) => (
                <label key={model.id} className={checkboxRowClass}>
                  <input
                    type="checkbox"
                    checked={selectedModelIds.includes(model.id)}
                    onChange={() =>
                      toggleId(selectedModelIds, setSelectedModelIds, model.id)
                    }
                    className="mt-1"
                  />
                  <span>
                    <span className="block font-medium text-white">
                      {model.label}
                    </span>
                    <span className="block text-xs text-slate-400">
                      {model.id}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-white">
              Extraits ({selectedSampleIds.length}/{samples.length})
            </h3>
            <div className="mt-2 space-y-2 max-h-64 overflow-y-auto pr-1">
              {samples.map((sample) => (
                <label key={sample.id} className={checkboxRowClass}>
                  <input
                    type="checkbox"
                    checked={selectedSampleIds.includes(sample.id)}
                    onChange={() =>
                      toggleId(
                        selectedSampleIds,
                        setSelectedSampleIds,
                        sample.id,
                      )
                    }
                    className="mt-1"
                  />
                  <span>
                    <span className="block font-medium text-white">
                      Extrait {sample.id} ({sample.reference_word_count} mots)
                    </span>
                    <span className="block text-xs text-slate-400 line-clamp-2">
                      {sample.reference_text?.slice(0, 120)}...
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={runBenchmark}
            disabled={
              isRunning ||
              loadingCatalog ||
              !apiToken ||
              selectedModelIds.length === 0 ||
              selectedSampleIds.length === 0
            }
            className="inline-flex items-center gap-2 rounded-xl bg-cyan-500/90 px-4 py-2 text-sm font-semibold text-slate-950 disabled:opacity-40"
          >
            {isRunning ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
            Lancer le benchmark
          </button>
          {isRunning && (
            <button
              type="button"
              onClick={cancelBenchmark}
              className="inline-flex items-center gap-2 rounded-xl border border-red-400/50 px-4 py-2 text-sm font-semibold text-red-300"
            >
              <Square className="h-4 w-4" />
              Annuler
            </button>
          )}
          {currentTask && (
            <p className="text-sm text-slate-300">{currentTask}</p>
          )}
        </div>

        {log.length > 0 && (
          <div className="mt-4 max-h-48 overflow-y-auto rounded-xl border border-white/10 bg-slate-950/40 p-3 text-xs space-y-1">
            {log.map((entry) => (
              <p
                key={entry.id}
                className={
                  entry.level === "error"
                    ? "text-red-300"
                    : entry.level === "warning"
                      ? "text-amber-300"
                      : "text-slate-300"
                }
              >
                [{entry.timestamp}] {entry.message}
              </p>
            ))}
          </div>
        )}
      </div>

      {results.length > 0 && <MedDictateBenchmarkResults results={results} />}
    </div>
  );
};

export default MedDictateBenchmark;
